import React, { useCallback, useContext, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";

import {
  Button,
  DialogContent,
  DialogTitle,
  EnhancedTableHead,
} from "../index";

import AddressBookService from "../../services/address_book";
import { reduceText, sortOrder } from "../../lib";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

const headCells = [
  { id: "name", sortable: true, label: "장소명" },
  { id: "addressBase", sortable: false, label: "주소" },
  {
    id: "placeInfoForCustomer",
    sortable: false,
    label: "장소 정보 및 특이사항",
  },
  { id: "phone", sortable: false, label: "연락처" },
  { id: "select", sortable: false, label: "선택" },
];

const useStyles = makeStyles(() => ({
  buttonClose: {
    position: "absolute",
    top: 22,
    right: 21,
  },
  dialogContent: {
    paddingBottom: 24,
    height: 700,
    minWidth: 960,
    overflowX: "hidden",
    overflow: "auto",
  },
  table: {
    padding: 0,
    backgroundColor: "#ffffff",
    borderRadius: 3.8,
    border: "solid 1px #ebedf8",
  },
  searchField: {
    width: 200,
    marginBottom: 20,
  },
  searchButton: {
    marginLeft: 10,
  },
}));

const TableData = (props: any) => {
  const { data, index, select } = props;

  return (
    <TableRow key={index}>
      <TableCell>{data.name}</TableCell>
      <TableCell>{data.addressBase + " " + data.addressDetail}</TableCell>
      <TableCell>{reduceText(data.placeInfoForCustomer || "", 20)}</TableCell>
      <TableCell>{data.phone}</TableCell>
      <TableCell>
        {data.name !== "" ? (
          <Button
            onClick={() => {
              console.log("운송주문-주소록 선택완료 및 닫기");
              window.gtag("event", `운송주문-주소록 선택완료 및 닫기`, {
                user_id: localStorage.getItem("customerName") || "none",
              });
              select(data);
            }}
          >
            선택
          </Button>
        ) : (
          <></>
        )}
      </TableCell>
    </TableRow>
  );
};

export default function AddressBook(props: any) {
  const classes = useStyles();
  const { name, open, close, setAddressForm, setLatest } = props;

  const [addressBook, setAddressBook] = useState<any>([]);
  const [searchResult, setSearchResult] = useState<[]>([]);
  const [orderType, setOrderType] = useState<string>("desc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");

  const navigate = useNavigate();
  const { register, watch, setValue } = useForm();

  const closeDialog = useCallback(() => {
    setSearchResult([]);
    close();
  }, [setSearchResult, close]);

  const updateSidebar = useCallback(
    (place: any) => {
      if (name === "departure") {
        setValue("departureName", place.name);
        setValue("departurePhone", place.phone);
      } else {
        setValue("arrivalName", place.name);
        setValue("arrivalPhone", place.phone);
      }
    },
    [setAddressForm, name, setValue]
  );

  const handleRequestSort = useCallback(
    (event: any, property: any) => {
      const isAsc = orderBy === property && orderType === "asc";
      setOrderType(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderType, orderBy, setOrderType, setOrderBy]
  );

  const inquiry = useCallback(async () => {
    try {
      const addressBookService = new AddressBookService(navigate);
      const response = await addressBookService.inquiry();
      setAddressBook(response.data.addressBook);
      setSearchResult(response.data.addressBook);
    } catch (e) {
      console.error("failed to handle addressBook:", e);
    }
  }, [setAddressBook, setSearchResult, navigate]);

  const select = useCallback(
    (a: any) => {
      setAddressForm(a);
      updateSidebar(a);
      if (name === "departure" || name === "arrival") setLatest("");
      closeDialog();
    },
    [setAddressForm, close]
  );

  const searchAddress = () => {
    const data = watch();
    const query = data.query;
    let result = addressBook.filter(
      (v: any) => v.name.includes(query) || v.addressBase.includes(query)
    );
    setSearchResult(result);
  };

  const handleKeyDown = useCallback(
    async (e: any) => {
      if (e.key === "Enter") {
        e.preventDefault(); // form submit (서버 전공) 막음
        e.stopPropagation(); // form submit event 막기

        await searchAddress();
      }
    },
    [searchAddress]
  );

  useEffect(() => {
    open && queueMicrotask(inquiry);
    setValue("query", "");
  }, [open, inquiry]);

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="lg"
      aria-labelledby="confirmation-dialog-title"
      open={open}
    >
      <IconButton
        size="small"
        onClick={closeDialog}
        className={classes.buttonClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle>주소록</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <form onKeyDown={handleKeyDown}>
          <Grid container direction={"column"}>
            <Grid>
              <TextField
                {...register("query")}
                className={classes.searchField}
                defaultValue={""}
                variant={"standard"}
                inputProps={{ maxLength: 50 }}
                placeholder={"장소명 또는 주소"}
              />
              <Button className={classes.searchButton} onClick={searchAddress}>
                검색
              </Button>
            </Grid>
            <Grid>
              {searchResult.length > 0 ? (
                <Table className={classes.table}>
                  <EnhancedTableHead
                    classes={classes}
                    orderBy={orderBy}
                    orderType={orderType}
                    onRequestSort={handleRequestSort}
                    headCells={headCells}
                  />
                  <TableBody>
                    {searchResult
                      .sort(sortOrder(orderType, orderBy))
                      .map((v: any, i: number) => (
                        <TableData data={v} index={i} select={select} />
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <Box
                  style={{
                    width: "100%",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Box
                    style={{
                      width: 100,
                      height: 100,
                      backgroundColor: "gray",
                      marginTop: 100,
                      marginLeft: "45%",
                      borderRadius: 50,
                      padding: 20,
                    }}
                  >
                    <SearchIcon
                      style={{ width: 60, height: 60, color: "#fff" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      mt: 3,
                      ml: 3,
                      fontSize: 20,
                      color: "#6a6a6a",
                      fontWeight: "bold",
                    }}
                  >
                    검색 결과가 없습니다.
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}
